'use client';

import { useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Building2,
    Zap,
    Shield,
    DollarSign,
    Bot,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    Sparkles,
    Users,
    CreditCard,
    Globe,
    Loader2,
    Copy,
    Check,
    Mail,
    Info,
} from 'lucide-react';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

// Categories for vendor registration
const CATEGORIES = [
    { value: 'telecommunications', label: 'Telecommunications', icon: '📱' },
    { value: 'utilities', label: 'Utilities', icon: '💡' },
    { value: 'streaming', label: 'Streaming Services', icon: '🎬' },
    { value: 'software', label: 'Software', icon: '💻' },
    { value: 'saas', label: 'SaaS', icon: '☁️' },
    { value: 'insurance', label: 'Insurance', icon: '🛡️' },
    { value: 'financial', label: 'Financial Services', icon: '🏦' },
    { value: 'travel', label: 'Travel', icon: '✈️' },
    { value: 'hospitality', label: 'Hospitality', icon: '🏨' },
    { value: 'retail', label: 'Retail', icon: '🛍️' },
    { value: 'food_delivery', label: 'Food Delivery', icon: '🍕' },
    { value: 'transportation', label: 'Transportation', icon: '🚗' },
    { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
    { value: 'education', label: 'Education', icon: '📚' },
    { value: 'other', label: 'Other', icon: '📦' },
];

const BUSINESS_TYPES = [
    { value: 'sole_proprietor', label: 'Sole Proprietor' },
    { value: 'llc', label: 'LLC' },
    { value: 'corporation', label: 'Corporation' },
    { value: 's_corp', label: 'S Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'nonprofit', label: 'Nonprofit' },
];

const PRICING_MODELS = [
    { value: 'subscription', label: 'Subscription (Recurring)' },
    { value: 'one_time', label: 'One-Time Purchase' },
    { value: 'usage_based', label: 'Usage-Based' },
    { value: 'tiered', label: 'Tiered Pricing' },
    { value: 'custom', label: 'Custom Pricing' },
];

export default function VendorPortalPage() {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const toast = useToast();

    // Form state
    const [formData, setFormData] = useState({
        // Business Info
        business_name: '',
        legal_name: '',
        business_type: '',
        website_url: '',
        description: '',

        // Contact Info
        contact_name: '',
        contact_email: '',
        contact_phone: '',

        // Address
        address_street: '',
        address_city: '',
        address_state: '',
        address_zip: '',
        address_country: 'US',

        // Services
        category: '',
        services_offered: '',
        pricing_model: '',
        min_price: '',
        max_price: '',

        // Terms
        terms_accepted: false,
    });

    const supabase = createClient();

    const updateField = (field: string, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Validation for each step
    const validateStep = (stepNumber: number): boolean => {
        const errors: Record<string, string> = {};

        if (stepNumber === 1) {
            if (!formData.business_name.trim()) errors.business_name = 'Business name is required';
            if (!formData.business_type) errors.business_type = 'Please select a business type';
            if (!formData.description.trim()) errors.description = 'Business description is required';
        }

        if (stepNumber === 2) {
            if (!formData.contact_name.trim()) errors.contact_name = 'Contact name is required';
            if (!formData.contact_email.trim()) errors.contact_email = 'Email is required';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
                errors.contact_email = 'Please enter a valid email address';
            }
            if (!formData.contact_phone.trim()) errors.contact_phone = 'Phone number is required';
        }

        if (stepNumber === 3) {
            if (!formData.category) errors.category = 'Please select a category';
            if (!formData.pricing_model) errors.pricing_model = 'Please select a pricing model';
        }

        if (stepNumber === 4) {
            if (!formData.terms_accepted) errors.terms_accepted = 'You must accept the terms to continue';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleContinue = () => {
        if (validateStep(step)) {
            setStep(s => s + 1);
        } else {
            toast.error('Please fill in all required fields');
        }
    };

    const handleSubmit = useCallback(async () => {
        // Validate terms acceptance
        if (!validateStep(4)) {
            toast.error('Please accept the terms and conditions');
            return;
        }

        setIsSubmitting(true);

        try {
            const vendorData = {
                business_name: formData.business_name,
                legal_name: formData.legal_name || undefined,
                business_type: formData.business_type,
                website_url: formData.website_url || undefined,
                description: formData.description,
                contact_name: formData.contact_name,
                contact_email: formData.contact_email,
                contact_phone: formData.contact_phone,
                address_street: formData.address_street || undefined,
                address_city: formData.address_city || undefined,
                address_state: formData.address_state || undefined,
                address_zip: formData.address_zip || undefined,
                address_country: formData.address_country || undefined,
                category: formData.category,
                services_offered: formData.services_offered || undefined,
                pricing_model: formData.pricing_model,
                min_price: formData.min_price || undefined,
                max_price: formData.max_price || undefined,
            };

            // Send vendor application via edge function
            const { data, error } = await supabase.functions.invoke('vendor-registration-email', {
                body: vendorData
            });

            if (error) throw error;

            if (data?.success) {
                setIsComplete(true);
                toast.success('Application submitted successfully!');
            } else {
                throw new Error(data?.error || 'Submission failed');
            }
        } catch (error: any) {
            console.error('Registration error:', error);
            toast.error(error.message || 'Failed to submit application. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, supabase, validateStep]);

    const copyApiKey = () => {
        if (apiKey) {
            navigator.clipboard.writeText(apiKey);
            setCopied(true);
            toast.success('API key copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Success Screen
    if (isComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
                <div className="container max-w-2xl mx-auto py-20 px-4">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-6">
                            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                        </div>
                        <h1 className="text-3xl font-bold mb-4 text-white">Application Submitted!</h1>
                        <p className="text-slate-400">
                            Thank you for your interest in joining the LinkBox Vendor Marketplace.
                        </p>
                    </div>

                    <Card className="mb-6 bg-gray-900/80 border-gray-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-white">
                                <Mail className="h-5 w-5 text-emerald-400" />
                                What's Next?
                            </CardTitle>
                            <CardDescription className="text-slate-400">
                                We'll review your application and get back to you at <span className="text-emerald-400">{formData.contact_email}</span>
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-sm font-medium text-emerald-400">1</div>
                                <div>
                                    <p className="font-medium text-white">Application Review</p>
                                    <p className="text-sm text-slate-400">Our team will review your business details within 1-2 business days.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-sm font-medium text-emerald-400">2</div>
                                <div>
                                    <p className="font-medium text-white">Approval Email</p>
                                    <p className="text-sm text-slate-400">You'll receive an email with your vendor credentials and next steps.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-sm font-medium text-emerald-400">3</div>
                                <div>
                                    <p className="font-medium text-white">Setup Your Store</p>
                                    <p className="text-sm text-slate-400">Connect Stripe and list your products/services in the vendor dashboard.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-sm font-medium text-emerald-400">4</div>
                                <div>
                                    <p className="font-medium text-white">Start Receiving Orders</p>
                                    <p className="text-sm text-slate-400">LinkBox AI agents will connect customers with your business.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gray-900/80 border-gray-800">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
                                <p className="text-sm text-slate-300">
                                    Questions? Contact us at <a href="mailto:support@linkboxai.com" className="text-emerald-400 hover:underline">support@linkboxai.com</a>
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="text-center mt-8">
                        <Link href="/">
                            <Button className="bg-transparent border-2 border-emerald-500/50 text-white hover:bg-emerald-500/10 hover:border-emerald-400">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
            {/* Navigation - Matching Main Site Branding */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3 flex-shrink-0">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl flex-shrink-0"
                                unoptimized
                            />
                            <span className="text-xl font-bold whitespace-nowrap">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 whitespace-nowrap">
                                Vendors
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <Link href="/ecosystem" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Ecosystem</Link>
                            <Link href="/vendor" className="text-[#4ade80] transition-colors text-sm font-medium">Vendor</Link>
                            <Link href="/investors" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Investors</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link
                                href="/vendor/login"
                                className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2"
                            >
                                Already a vendor?
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="container max-w-6xl mx-auto pt-24 pb-12 px-4">
                <div className="grid lg:grid-cols-5 gap-12">
                    {/* Left Side - Benefits */}
                    <div className="lg:col-span-2">
                        <div className="sticky top-24">
                            <h1 className="text-4xl font-bold mb-4 text-white">
                                Join the Future of<br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                                    Agentic Commerce
                                </span>
                            </h1>
                            <p className="text-lg text-slate-400 mb-8">
                                Register as a LinkBox vendor and let AI agents connect your business with millions of users seamlessly.
                            </p>

                            <div className="space-y-6">
                                <BenefitCard
                                    icon={Bot}
                                    title="AI-Powered Discovery"
                                    description="Users' AI agents find and recommend your services based on their needs."
                                />
                                <BenefitCard
                                    icon={DollarSign}
                                    title="Direct Payments"
                                    description="Receive payments directly from customers through our secure payment system."
                                />
                                <BenefitCard
                                    icon={Shield}
                                    title="User Consent"
                                    description="All transactions require user signature, ensuring trust and compliance."
                                />
                                <BenefitCard
                                    icon={Zap}
                                    title="Instant Integration"
                                    description="Simple API integration to automate orders and fulfillment."
                                />
                            </div>

                            <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <Users className="h-5 w-5 text-emerald-400" />
                                    <span className="font-semibold text-white">Join 500+ vendors</span>
                                </div>
                                <p className="text-sm text-slate-400">
                                    Already processing $2M+ in agentic transactions monthly.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Registration Form */}
                    <div className="lg:col-span-3">
                        <Card className="shadow-xl bg-gray-900/80 border-gray-800 backdrop-blur-sm">
                            <CardHeader>
                                <div className="flex items-center justify-between mb-4">
                                    <CardTitle className="text-white">Vendor Registration</CardTitle>
                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4].map((s) => (
                                            <div
                                                key={s}
                                                className={cn(
                                                    'w-8 h-1 rounded-full transition-colors',
                                                    s <= step ? 'bg-emerald-500' : 'bg-gray-700'
                                                )}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <CardDescription className="text-slate-400">
                                    {step === 1 && 'Tell us about your business'}
                                    {step === 2 && 'Contact information'}
                                    {step === 3 && 'Services & pricing'}
                                    {step === 4 && 'Review & submit'}
                                </CardDescription>
                            </CardHeader>

                            <CardContent>
                                {/* Step 1: Business Info */}
                                {step === 1 && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="col-span-2">
                                                <Label htmlFor="business_name" className={fieldErrors.business_name ? 'text-red-400' : 'text-slate-300'}>
                                                    Business Name *
                                                </Label>
                                                <Input
                                                    id="business_name"
                                                    placeholder="Acme Corp"
                                                    value={formData.business_name}
                                                    onChange={(e) => updateField('business_name', e.target.value)}
                                                    className={fieldErrors.business_name ? 'border-red-500' : ''}
                                                />
                                                {fieldErrors.business_name && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.business_name}</p>
                                                )}
                                            </div>

                                            <div className="col-span-2">
                                                <Label htmlFor="legal_name" className="text-slate-300">Legal Business Name</Label>
                                                <Input
                                                    id="legal_name"
                                                    placeholder="Acme Corporation Inc."
                                                    value={formData.legal_name}
                                                    onChange={(e) => updateField('legal_name', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <Label className={fieldErrors.business_type ? 'text-red-400' : 'text-slate-300'}>
                                                    Business Type *
                                                </Label>
                                                <Select
                                                    value={formData.business_type}
                                                    onValueChange={(v) => updateField('business_type', v)}
                                                >
                                                    <SelectTrigger className={fieldErrors.business_type ? 'border-red-500' : ''}>
                                                        <SelectValue placeholder="Select type" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {BUSINESS_TYPES.map((type) => (
                                                            <SelectItem key={type.value} value={type.value}>
                                                                {type.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {fieldErrors.business_type && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.business_type}</p>
                                                )}
                                            </div>

                                            <div>
                                                <Label htmlFor="website_url" className="text-slate-300">Website</Label>
                                                <Input
                                                    id="website_url"
                                                    placeholder="https://acme.com"
                                                    value={formData.website_url}
                                                    onChange={(e) => updateField('website_url', e.target.value)}
                                                />
                                            </div>

                                            <div className="col-span-2">
                                                <Label htmlFor="description" className={fieldErrors.description ? 'text-red-400' : 'text-slate-300'}>
                                                    Business Description *
                                                </Label>
                                                <Textarea
                                                    id="description"
                                                    placeholder="Tell us about your business and the services you offer..."
                                                    rows={3}
                                                    value={formData.description}
                                                    onChange={(e) => updateField('description', e.target.value)}
                                                    className={fieldErrors.description ? 'border-red-500' : ''}
                                                />
                                                {fieldErrors.description && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Contact Info */}
                                {step === 2 && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="col-span-2">
                                                <Label htmlFor="contact_name" className={fieldErrors.contact_name ? 'text-red-400' : 'text-slate-300'}>
                                                    Contact Name *
                                                </Label>
                                                <Input
                                                    id="contact_name"
                                                    placeholder="John Smith"
                                                    value={formData.contact_name}
                                                    onChange={(e) => updateField('contact_name', e.target.value)}
                                                    className={fieldErrors.contact_name ? 'border-red-500' : ''}
                                                />
                                                {fieldErrors.contact_name && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.contact_name}</p>
                                                )}
                                            </div>

                                            <div>
                                                <Label htmlFor="contact_email" className={fieldErrors.contact_email ? 'text-red-400' : 'text-slate-300'}>
                                                    Email *
                                                </Label>
                                                <Input
                                                    id="contact_email"
                                                    type="email"
                                                    placeholder="john@acme.com"
                                                    value={formData.contact_email}
                                                    onChange={(e) => updateField('contact_email', e.target.value)}
                                                    className={fieldErrors.contact_email ? 'border-red-500' : ''}
                                                />
                                                {fieldErrors.contact_email && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.contact_email}</p>
                                                )}
                                            </div>

                                            <div>
                                                <Label htmlFor="contact_phone" className={fieldErrors.contact_phone ? 'text-red-400' : 'text-slate-300'}>
                                                    Phone *
                                                </Label>
                                                <Input
                                                    id="contact_phone"
                                                    placeholder="+1 (555) 123-4567"
                                                    value={formData.contact_phone}
                                                    onChange={(e) => updateField('contact_phone', e.target.value)}
                                                    className={fieldErrors.contact_phone ? 'border-red-500' : ''}
                                                />
                                                {fieldErrors.contact_phone && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.contact_phone}</p>
                                                )}
                                            </div>

                                            <div className="col-span-2">
                                                <Label htmlFor="address_street" className="text-slate-300">Street Address</Label>
                                                <Input
                                                    id="address_street"
                                                    placeholder="123 Business Ave"
                                                    value={formData.address_street}
                                                    onChange={(e) => updateField('address_street', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="address_city" className="text-slate-300">City</Label>
                                                <Input
                                                    id="address_city"
                                                    placeholder="New York"
                                                    value={formData.address_city}
                                                    onChange={(e) => updateField('address_city', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="address_state" className="text-slate-300">State</Label>
                                                <Input
                                                    id="address_state"
                                                    placeholder="NY"
                                                    value={formData.address_state}
                                                    onChange={(e) => updateField('address_state', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="address_zip" className="text-slate-300">ZIP Code</Label>
                                                <Input
                                                    id="address_zip"
                                                    placeholder="10001"
                                                    value={formData.address_zip}
                                                    onChange={(e) => updateField('address_zip', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="address_country" className="text-slate-300">Country</Label>
                                                <Input
                                                    id="address_country"
                                                    placeholder="US"
                                                    value={formData.address_country}
                                                    onChange={(e) => updateField('address_country', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Services & Pricing */}
                                {step === 3 && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="col-span-2">
                                                <Label className={fieldErrors.category ? 'text-red-400' : 'text-slate-300'}>
                                                    Business Category *
                                                </Label>
                                                <Select
                                                    value={formData.category}
                                                    onValueChange={(v) => updateField('category', v)}
                                                >
                                                    <SelectTrigger className={fieldErrors.category ? 'border-red-500' : ''}>
                                                        <SelectValue placeholder="Select category" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {CATEGORIES.map((cat) => (
                                                            <SelectItem key={cat.value} value={cat.value}>
                                                                <span className="flex items-center gap-2">
                                                                    <span>{cat.icon}</span>
                                                                    <span>{cat.label}</span>
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {fieldErrors.category && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.category}</p>
                                                )}
                                            </div>

                                            <div className="col-span-2">
                                                <Label htmlFor="services_offered" className="text-slate-300">Services Offered</Label>
                                                <Textarea
                                                    id="services_offered"
                                                    placeholder="List your services, separated by commas (e.g., Phone Plans, Internet, TV Bundles)"
                                                    rows={2}
                                                    value={formData.services_offered}
                                                    onChange={(e) => updateField('services_offered', e.target.value)}
                                                />
                                            </div>

                                            <div className="col-span-2">
                                                <Label className={fieldErrors.pricing_model ? 'text-red-400' : 'text-slate-300'}>
                                                    Pricing Model *
                                                </Label>
                                                <Select
                                                    value={formData.pricing_model}
                                                    onValueChange={(v) => updateField('pricing_model', v)}
                                                >
                                                    <SelectTrigger className={fieldErrors.pricing_model ? 'border-red-500' : ''}>
                                                        <SelectValue placeholder="Select pricing model" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {PRICING_MODELS.map((model) => (
                                                            <SelectItem key={model.value} value={model.value}>
                                                                {model.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {fieldErrors.pricing_model && (
                                                    <p className="text-red-400 text-sm mt-1">{fieldErrors.pricing_model}</p>
                                                )}
                                            </div>

                                            <div>
                                                <Label htmlFor="min_price" className="text-slate-300">Minimum Price (USD)</Label>
                                                <Input
                                                    id="min_price"
                                                    type="number"
                                                    placeholder="9.99"
                                                    value={formData.min_price}
                                                    onChange={(e) => updateField('min_price', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="max_price" className="text-slate-300">Maximum Price (USD)</Label>
                                                <Input
                                                    id="max_price"
                                                    type="number"
                                                    placeholder="99.99"
                                                    value={formData.max_price}
                                                    onChange={(e) => updateField('max_price', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Review & Submit */}
                                {step === 4 && (
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <ReviewSection title="Business Information">
                                                <ReviewItem label="Business Name" value={formData.business_name} />
                                                <ReviewItem label="Type" value={BUSINESS_TYPES.find(t => t.value === formData.business_type)?.label || ''} />
                                                <ReviewItem label="Website" value={formData.website_url} />
                                            </ReviewSection>

                                            <ReviewSection title="Contact">
                                                <ReviewItem label="Name" value={formData.contact_name} />
                                                <ReviewItem label="Email" value={formData.contact_email} />
                                                <ReviewItem label="Phone" value={formData.contact_phone} />
                                            </ReviewSection>

                                            <ReviewSection title="Services">
                                                <ReviewItem label="Category" value={CATEGORIES.find(c => c.value === formData.category)?.label || ''} />
                                                <ReviewItem label="Pricing" value={PRICING_MODELS.find(m => m.value === formData.pricing_model)?.label || ''} />
                                                <ReviewItem
                                                    label="Price Range"
                                                    value={`$${formData.min_price || '0'} - $${formData.max_price || '0'}`}
                                                />
                                            </ReviewSection>
                                        </div>

                                        <div className={`border-t pt-4 ${fieldErrors.terms_accepted ? 'border-red-500' : 'border-gray-700'}`}>
                                            <div className="flex items-start space-x-3">
                                                <Checkbox
                                                    id="terms"
                                                    checked={formData.terms_accepted}
                                                    onCheckedChange={(checked) => updateField('terms_accepted', checked === true)}
                                                    className={fieldErrors.terms_accepted ? 'border-red-500' : ''}
                                                />
                                                <label htmlFor="terms" className={`text-sm leading-relaxed ${fieldErrors.terms_accepted ? 'text-red-400' : 'text-slate-400'}`}>
                                                    I agree to the{' '}
                                                    <Link href="/legal/vendor-terms" className="text-emerald-400 hover:underline">
                                                        Vendor Terms of Service
                                                    </Link>{' '}
                                                    and{' '}
                                                    <Link href="/legal/privacy" className="text-emerald-400 hover:underline">
                                                        Privacy Policy
                                                    </Link>
                                                    . I understand that LinkBox will collect a platform fee on transactions.
                                                </label>
                                            </div>
                                            {fieldErrors.terms_accepted && (
                                                <p className="text-red-400 text-sm mt-2">{fieldErrors.terms_accepted}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Navigation Buttons */}
                                <div className="flex items-center justify-between mt-8 pt-4 border-t">
                                    {step > 1 ? (
                                        <Button variant="ghost" onClick={() => setStep(s => s - 1)}>
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </Button>
                                    ) : (
                                        <div />
                                    )}

                                    {step < 4 ? (
                                        <Button onClick={handleContinue} className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700">
                                            Continue
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleSubmit}
                                            disabled={!formData.terms_accepted || isSubmitting}
                                            className="bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700"
                                        >
                                            {isSubmitting ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                            )}
                                            Submit Application
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Footer - Matching Main Site Branding */}
            <footer className="bg-gray-950 border-t border-gray-800 py-12 mt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                                unoptimized
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
                            <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                            <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
                            <Link href="/vendor" className="hover:text-white transition-colors">Vendor Portal</Link>
                            <Link href="/security" className="hover:text-white transition-colors">Security</Link>
                            <Link href="mailto:support@linkboxai.com" className="hover:text-white transition-colors">Contact</Link>
                        </div>
                        <p className="text-sm text-gray-500">© 2026 BB23 LLC. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

// Helper Components

function BenefitCard({
    icon: Icon,
    title,
    description
}: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
}) {
    return (
        <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
                <h3 className="font-semibold mb-1 text-white">{title}</h3>
                <p className="text-sm text-slate-400">{description}</p>
            </div>
        </div>
    );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="font-medium mb-2 text-sm text-emerald-400">{title}</h4>
            <div className="space-y-1">{children}</div>
        </div>
    );
}

function ReviewItem({ label, value }: { label: string; value: string }) {
    if (!value) return null;
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">{label}</span>
            <span className="font-medium text-white">{value}</span>
        </div>
    );
}
